import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, message, Input, Card, Tag, Row, Col, Typography, Select, Switch,
  Modal, Form, InputNumber
} from 'antd';
import { 
  SearchOutlined, FileTextOutlined, PlusOutlined, UserOutlined, CarOutlined,
  EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchCustomerVehicles,
  fetchServiceOrders,
  createCustomerVehicle,
  updateCustomerVehicle,
  deleteCustomerVehicle,
  VEHICLE_ASSIGNED_TO_OTHER_CODE,
  type CustomerVehicleDto,
  type ServiceOrderDto
} from '../services/serviceOrderService';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_ADMIN, ROLE_YONETICI } from '../utils/permissions';
import { trLower, trUpperNormalize } from '../utils/trLocale';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;

const formatPhone = (phone: string | undefined): string => {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '').slice(0, 10);
  if (!digits) return phone;
  return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
    [a, b, c, d].filter(Boolean).join(' ')
  );
};

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  phone?: string;
  email?: string;
  lastPlate?: string; // Son plaka bilgisi
}

const MOBILE_BREAKPOINT = 768;

const ServiceCustomers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleCodes = user?.roleCodes ?? [];
  const isAdmin = roleCodes.includes(ROLE_ADMIN);
  const canEditVehicle = roleCodes.includes(ROLE_ADMIN) || roleCodes.includes(ROLE_YONETICI);
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [customers, setCustomers] = useState<Account[]>([]);
  const [allCustomers, setAllCustomers] = useState<Account[]>([]); // Tüm müşteriler (filtreleme için)
  const [allVehicles, setAllVehicles] = useState<CustomerVehicleDto[]>([]); // Tüm araçlar (filtreleme için)
  const [vehicles, setVehicles] = useState<CustomerVehicleDto[]>([]);
  const [orders, setOrders] = useState<ServiceOrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [vehicleForm] = Form.useForm();
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>();
  const [customerModalVisible, setCustomerModalVisible] = useState(false);
  const [customerForm] = Form.useForm();
  const [customerCategories, setCustomerCategories] = useState<Array<{ value: number; name: string; displayName: string }>>([]);
  const [editVehicleModalVisible, setEditVehicleModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<CustomerVehicleDto | null>(null);
  const [editVehicleForm] = Form.useForm();
  const [editVehicleBrandId, setEditVehicleBrandId] = useState<number | undefined>();

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // URL'den customerId ve vehicleId varsa sayfa açılışında seç (örn. /service-customers?customerId=1&vehicleId=5)
  useEffect(() => {
    const customerIdParam = searchParams.get('customerId');
    if (customerIdParam) {
      const id = parseInt(customerIdParam, 10);
      if (!isNaN(id)) setSelectedCustomerId(id);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedCustomerId) {
      setSelectedVehicleId(null);
      loadVehiclesForCustomer(selectedCustomerId);
    } else {
      setVehicles([]);
      setOrders([]);
      setSelectedVehicleId(null);
    }
  }, [selectedCustomerId]);

  // Araçlar yüklendiğinde: URL'de vehicleId varsa onu seç, yoksa son eklenen aracı seç
  useEffect(() => {
    if (vehicles.length === 0) return;
    const vehicleIdParam = searchParams.get('vehicleId');
    if (vehicleIdParam) {
      const vid = parseInt(vehicleIdParam, 10);
      if (!isNaN(vid) && vehicles.some((v) => v.id === vid)) {
        setSelectedVehicleId(vid);
        return;
      }
    }
    if (selectedVehicleId === null) {
      const lastAdded = [...vehicles].sort((a, b) => b.id - a.id)[0];
      setSelectedVehicleId(lastAdded.id);
    }
  }, [vehicles, searchParams]);

  // Servis fişlerini seçilen araca göre yükle
  useEffect(() => {
    if (selectedCustomerId && selectedVehicleId) {
      loadOrdersForCustomer(selectedCustomerId, selectedVehicleId);
    } else {
      setOrders([]);
    }
  }, [selectedCustomerId, selectedVehicleId, showHistory]);

  useEffect(() => {
    loadBrands();
    loadCustomerCategories();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      loadModelsForBrand(selectedBrandId);
    } else {
      setModels([]);
      vehicleForm.setFieldsValue({ modelId: undefined });
    }
  }, [selectedBrandId]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/accounts');
      // Sadece müşterileri filtrele (AccountType = Customer veya Both)
      const filtered = (data || []).filter((acc: Account) =>
        acc.accountType === 'Customer' || acc.accountType === 'Both'
      );
      // Aynı id ile tekrar gelen kayıtları tekilleştir (VT'de 1 kayıt olsa bile listede bir kez görünsün)
      const byId = new Map<number, Account>();
      filtered.forEach((acc: Account) => byId.set(acc.id, acc));
      let customerAccounts = Array.from(byId.values());

      // Tüm araçları yükle
      try {
        const vehiclesData = await fetchCustomerVehicles();
        setAllVehicles(vehiclesData);

        const vehiclesByCustomer = new Map<number, string>();
        vehiclesData.forEach(vehicle => {
          if (!vehiclesByCustomer.has(vehicle.customerId)) {
            vehiclesByCustomer.set(vehicle.customerId, vehicle.plate);
          }
        });

        customerAccounts = customerAccounts.map((acc: Account) => ({
          ...acc,
          lastPlate: vehiclesByCustomer.get(acc.id)
        }));
      } catch (vehicleError) {
        console.error('Araçlar yüklenirken hata:', vehicleError);
      }

      setAllCustomers(customerAccounts);
      setCustomers(customerAccounts);
    } catch (error) {
      message.error('Müşteriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadVehiclesForCustomer = async (customerId: number) => {
    try {
      const data = await fetchCustomerVehicles({ customerId });
      setVehicles(data);
    } catch (error) {
      console.error('Araçlar yüklenirken hata:', error);
    }
  };

  const loadOrdersForCustomer = async (customerId: number, vehicleId?: number | null) => {
    try {
      setOrdersLoading(true);
      const data = await fetchServiceOrders({ 
        customerId,
        ...(vehicleId ? { vehicleId } : {}),
        onlyOpen: !showHistory // Sadece açık fişler veya hepsi
      });
      setOrders(data);
    } catch (error) {
      message.error('Servis fişleri yüklenirken hata oluştu');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    
    if (!value.trim()) {
      // Arama terimi boşsa tüm müşterileri göster
      const customersWithPlates = allCustomers.map((acc: Account) => {
        const customerVehicle = allVehicles.find(v => v.customerId === acc.id);
        return {
          ...acc,
          lastPlate: customerVehicle?.plate
        };
      });
      setCustomers(customersWithPlates);
      return;
    }

    const searchLower = trLower(value.trim());

    // Plaka ile eşleşen araçların müşteri ID'lerini bul (Türkçe İ/i duyarsız)
    const customerIdsFromVehicles = new Set<number>();
    allVehicles.forEach(vehicle => {
      if (trLower(vehicle.plate).includes(searchLower)) {
        customerIdsFromVehicles.add(vehicle.customerId);
      }
    });

    // Müşterileri filtrele: ad, soyad, ad soyad, telefon veya plaka (Türkçe İ/i duyarsız, canlı sunucu dili fark etmez)
    const filteredCustomers = allCustomers.filter((acc: Account) => {
      if (customerIdsFromVehicles.has(acc.id)) {
        return true;
      }
      const firstName = trLower(acc.accountName?.split(' ')[0]);
      const lastName = trLower(acc.accountName?.split(' ').slice(1).join(' '));
      const fullName = trLower(acc.accountName);
      if (firstName.includes(searchLower) || lastName.includes(searchLower) || fullName.includes(searchLower)) {
        return true;
      }
      if (acc.phone && trLower(acc.phone).includes(searchLower)) {
        return true;
      }
      return false;
    });

    // Aynı id birden fazla gelmesin (API join vb. tekrarı önlemek için)
    const uniqueById = new Map<number, Account>();
    filteredCustomers.forEach((acc) => uniqueById.set(acc.id, acc));
    const uniqueFiltered = Array.from(uniqueById.values());

    const customersWithPlates = uniqueFiltered.map((acc: Account) => {
      const customerVehicle = allVehicles.find(v => v.customerId === acc.id);
      return {
        ...acc,
        lastPlate: customerVehicle?.plate
      };
    });

    setCustomers(customersWithPlates);
  };

  const handleSelectCustomer = (customer: Account) => {
    setSelectedCustomerId(customer.id);
  };

  const handleCreateServiceOrder = () => {
    if (!selectedCustomerId) {
      message.warning('Lütfen önce bir müşteri seçin');
      return;
    }
    const params = new URLSearchParams({ customerId: String(selectedCustomerId) });
    if (selectedVehicleId) params.set('vehicleId', String(selectedVehicleId));
    navigate(`/service-orders/add?${params.toString()}`);
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`/service-orders/${orderId}`);
  };

  const loadBrands = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch (error) {
      console.error('Markalar yüklenirken hata:', error);
    }
  };

  const loadModelsForBrand = async (brandId: number) => {
    try {
      const data = await fetchModels(brandId);
      setModels(data);
    } catch (error) {
      console.error('Modeller yüklenirken hata:', error);
    }
  };

  const handleAddVehicle = () => {
    if (!selectedCustomerId) {
      message.warning('Lütfen önce bir müşteri seçin');
      return;
    }
    vehicleForm.resetFields();
    vehicleForm.setFieldsValue({ currentKm: 0 });
    setSelectedBrandId(undefined);
    setModels([]);
    setVehicleModalVisible(true);
  };

  const saveVehiclePayload = (reassign = false) => {
    const values = vehicleForm.getFieldsValue();
    const rawPlate = (values.plate != null ? String(values.plate).trim() : '');
    const plate = rawPlate !== '' ? rawPlate.toLocaleUpperCase('tr-TR') : rawPlate;
    return {
      customerId: selectedCustomerId!,
      plate,
      vinChassis: values.vinChassis,
      brandId: values.brandId,
      modelId: values.modelId,
      make: values.make,
      model: values.model,
      year: values.year,
      color: values.color,
      engineNo: values.engineNo,
      currentKm: values.currentKm || 0,
      notes: values.notes,
      reassignToNewCustomer: reassign
    };
  };

  const handleSaveVehicle = async () => {
    try {
      await vehicleForm.validateFields();
      if (!selectedCustomerId) {
        message.error('Müşteri seçilmedi');
        return;
      }

      await createCustomerVehicle(saveVehiclePayload(false));
      message.success('Araç eklendi');
      setVehicleModalVisible(false);
      vehicleForm.resetFields();
      loadVehiclesForCustomer(selectedCustomerId);
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.code === VEHICLE_ASSIGNED_TO_OTHER_CODE) {
        const { plate, currentCustomerName, newCustomerName } = data;
        Modal.confirm({
          title: 'Araç atama onayı',
          content: `${plate} plakalı araç ${currentCustomerName} adına kayıtlıdır. ${newCustomerName} müşterisine atanacaktır. Devam edilsin mi?`,
          okText: 'Evet',
          cancelText: 'İptal',
          onOk: async () => {
            try {
              await createCustomerVehicle(saveVehiclePayload(true));
              message.success('Araç yeni müşteriye atandı');
              setVehicleModalVisible(false);
              vehicleForm.resetFields();
              loadVehiclesForCustomer(selectedCustomerId!);
            } catch (err: any) {
              message.error(err.response?.data?.message || 'Atama yapılamadı');
            }
          }
        });
        return;
      }
      if (data?.message) {
        message.error(data.message);
      } else {
        message.error('Araç eklenirken hata oluştu');
      }
    }
  };

  const loadCustomerCategories = async () => {
    try {
      const { data } = await api.get('/accounts/customer-categories');
      setCustomerCategories(data);
    } catch (error) {
      console.error('Müşteri kategorileri yüklenirken hata:', error);
    }
  };

  const handleAddCustomer = () => {
    customerForm.resetFields();
    customerForm.setFieldsValue({
      customerCategory: 1, // Perakende
    });
    setCustomerModalVisible(true);
  };

  const handleSaveCustomer = async () => {
    try {
      const values = await customerForm.validateFields();
      
      const trUpper = (s: string | undefined) => (s != null && s !== '' ? String(s).toLocaleUpperCase('tr-TR') : s);
      const payload = {
        firstName: trUpper(values.firstName),
        lastName: trUpper(values.lastName),
        companyName: trUpper(values.companyName),
        accountType: 1, // Customer
        customerCategory: values.customerCategory,
        taxNumber: values.taxNumber,
        taxOffice: values.taxOffice,
        email: values.email,
        phone: values.phone ? String(values.phone).replace(/\s/g, '') : undefined,
        address: values.address,
        notificationConsent: true // Bildirim onayı varsayılan olarak açık (checkbox gösterilmiyor)
      };

      const { data } = await api.post('/accounts', payload);
      message.success('Müşteri eklendi');
      setCustomerModalVisible(false);
      customerForm.resetFields();
      await loadCustomers();
      // Yeni eklenen müşteriyi seç
      if (data && data.id) {
        setSelectedCustomerId(data.id);
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.errorFields) {
        // Form validation error
        return;
      } else {
        message.error('Müşteri eklenirken hata oluştu');
      }
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'blue';    // Kabul
      case 2: return 'orange';  // Ön Tespit
      case 3: return 'purple';  // Onarımda
      case 4: return 'green';  // Tamamlandı
      case 5: return 'default'; // Teslim Edildi
      default: return 'default';
    }
  };

  const customerColumns = [
    {
      title: 'Müşteri Adı',
      dataIndex: 'accountName',
      key: 'accountName',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string | undefined) => formatPhone(phone),
    },
    {
      title: 'Son Plaka',
      dataIndex: 'lastPlate',
      key: 'lastPlate',
      render: (plate: string) => plate || '-',
    },
  ];

  const handleEditVehicle = (v: CustomerVehicleDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingVehicle(v);
    setEditVehicleBrandId(v.brandId);
    if (v.brandId) loadModelsForBrand(v.brandId);
    editVehicleForm.setFieldsValue({
      plate: v.plate,
      vinChassis: v.vinChassis,
      brandId: v.brandId,
      modelId: v.modelId,
      make: v.make,
      model: v.model,
      year: v.year,
      color: v.color,
      engineNo: v.engineNo,
      currentKm: v.currentKm ?? 0,
      notes: v.notes,
    });
    setEditVehicleModalVisible(true);
  };

  const handleSaveEditVehicle = async () => {
    if (!editingVehicle) return;
    try {
      await editVehicleForm.validateFields();
      const values = editVehicleForm.getFieldsValue();
      await updateCustomerVehicle(editingVehicle.id, {
        plate: editingVehicle.plate, // plaka değiştirilmez
        vinChassis: values.vinChassis,
        brandId: values.brandId,
        modelId: values.modelId,
        make: values.make,
        model: values.model,
        year: values.year,
        color: values.color,
        engineNo: values.engineNo,
        currentKm: values.currentKm ?? 0,
        notes: values.notes,
      });
      message.success('Araç güncellendi');
      setEditVehicleModalVisible(false);
      setEditingVehicle(null);
      if (selectedCustomerId) loadVehiclesForCustomer(selectedCustomerId);
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Güncelleme başarısız');
    }
  };

  const handleDeleteVehicle = (v: CustomerVehicleDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (v.canDelete === false) {
      message.warning('Bu araç servis fişi veya evrakta kullanıldığı için silinemez.');
      return;
    }
    Modal.confirm({
      title: 'Aracı sil',
      content: `${v.plate} plakalı aracı silmek istediğinize emin misiniz?`,
      okText: 'Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk: async () => {
        try {
          await deleteCustomerVehicle(v.id);
          message.success('Araç silindi');
          if (selectedCustomerId) loadVehiclesForCustomer(selectedCustomerId);
        } catch (err: any) {
          message.error(err.response?.data?.message || 'Silme başarısız');
        }
      },
    });
  };

  const vehicleColumns = [
    {
      title: 'Plaka',
      dataIndex: 'plate',
      key: 'plate',
    },
    {
      title: 'Marka/Model',
      key: 'info',
      render: (_: any, v: CustomerVehicleDto) => 
        `${v.brandName || v.make || ''} ${v.modelName || v.model || ''}`.trim() || '-',
    },
    {
      title: 'Yıl',
      dataIndex: 'year',
      key: 'year',
      render: (year: number) => year || '-',
    },
    {
      title: 'Renk',
      dataIndex: 'color',
      key: 'color',
      render: (color: string) => color || '-',
    },
    {
      title: 'Km',
      dataIndex: 'currentKm',
      key: 'currentKm',
      render: (km: number) => km?.toLocaleString('tr-TR') || '0',
    },
    ...(canEditVehicle || isAdmin ? [{
      title: '',
      key: 'actions',
      width: 90,
      render: (_: any, v: CustomerVehicleDto) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          {canEditVehicle && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => handleEditVehicle(v, e)}
              title="Düzenle"
            />
          )}
          {isAdmin && (
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={v.canDelete === false}
              onClick={(e) => handleDeleteVehicle(v, e)}
              title={v.canDelete === false ? 'Servis fişi veya evrakta kullanıldığı için silinemez' : 'Sil'}
            />
          )}
        </Space>
      ),
    }] : []),
  ];

  const orderColumns = [
    {
      title: 'Fiş No',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (text: string, record: ServiceOrderDto) => (
        <Button
          type="link"
          onClick={() => handleViewOrder(record.id)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Araç',
      dataIndex: 'vehiclePlate',
      key: 'vehiclePlate',
    },
    {
      title: 'Tarih',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: number, record: ServiceOrderDto) => (
        <Tag color={getStatusColor(status)}>{record.statusText}</Tag>
      ),
    },
    {
      title: 'Toplam',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `${amount.toFixed(2)} ₺`,
    },
  ];

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  return (
    <div className="service-customers-page">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <UserOutlined /> Servis Yönetimi - Müşteriler
        </Title>
        <Space wrap>
          {selectedCustomerId && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateServiceOrder}
            >
              Yeni Servis Fişi
            </Button>
          )}
        </Space>
      </div>

      <Row gutter={16}>
        {/* Sol Panel - Müşteri Listesi */}
        <Col xs={24} md={8}>
          <Card 
            title="Müşteriler" 
            extra={
              <Space wrap size="small">
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddCustomer}
                >
                  Müşteri Ekle
                </Button>
                <Search
                  placeholder="İsim, telefon veya plaka ara"
                  onSearch={handleSearch}
                  onChange={(e) => {
                    handleSearch(e.target.value);
                  }}
                  style={{ minWidth: 140, flex: '1 1 160px', maxWidth: 220 }}
                  allowClear
                />
              </Space>
            }
          >
            <Table
              columns={customerColumns}
              dataSource={customers}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10, showSizeChanger: !isMobile }}
              size="small"
              scroll={{ x: 320 }}
              onRow={(record) => ({
                onClick: () => handleSelectCustomer(record),
                style: { 
                  cursor: 'pointer',
                  backgroundColor: selectedCustomerId === record.id ? 'rgba(255,255,255,0.08)' : 'transparent'
                }
              })}
            />
          </Card>
        </Col>

        {/* Sağ Panel - Seçili Müşteri Detayları */}
        <Col xs={24} md={16}>
          {selectedCustomerId ? (
            <>
              {/* Müşteri Bilgileri */}
              <Card 
                title={
                  <Space wrap>
                    <UserOutlined />
                    <span>{selectedCustomer?.accountName}</span>
                    {selectedCustomer?.phone && (
                      <Text type="secondary">({formatPhone(selectedCustomer.phone)})</Text>
                    )}
                  </Space>
                }
                extra={
                  <Button onClick={() => setSelectedCustomerId(null)}>
                    Temizle
                  </Button>
                }
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Text strong>Hesap Kodu:</Text> {selectedCustomer?.accountCode}
                  </Col>
                  <Col xs={24} sm={8}>
                    <Text strong>Telefon:</Text> {formatPhone(selectedCustomer?.phone)}
                  </Col>
                  <Col xs={24} sm={8}>
                    <Text strong>E-posta:</Text> {selectedCustomer?.email || '-'}
                  </Col>
                </Row>
              </Card>

              {/* Araçlar */}
              <Card 
                title={<><CarOutlined /> Araçlar ({vehicles.length})</>}
                extra={
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={handleAddVehicle}
                  >
                    Araç Ekle
                  </Button>
                }
                style={{ marginBottom: 16 }}
              >
                <Table
                  columns={vehicleColumns}
                  dataSource={vehicles}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 480 }}
                  onRow={(record) => ({
                    onClick: () => setSelectedVehicleId(record.id),
                    style: { 
                      cursor: 'pointer',
                      backgroundColor: selectedVehicleId === record.id ? 'rgba(255,255,255,0.08)' : 'transparent'
                    }
                  })}
                />
              </Card>

              {/* Servis Fişleri */}
              <Card 
                title={
                  <Space>
                    <FileTextOutlined /> Servis Fişleri
                    <Switch
                      checkedChildren="Geçmiş"
                      unCheckedChildren="Açık"
                      checked={showHistory}
                      onChange={setShowHistory}
                    />
                  </Space>
                }
                extra={
                  <Text type="secondary">
                    {orders.length} fiş
                  </Text>
                }
              >
                <Table
                  columns={orderColumns}
                  dataSource={orders}
                  rowKey="id"
                  loading={ordersLoading}
                  pagination={{ pageSize: 10, showSizeChanger: !isMobile }}
                  size="small"
                  scroll={{ x: 460 }}
                />
              </Card>
            </>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <UserOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>Lütfen bir müşteri seçin</div>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Araç Ekleme Modal */}
      <Modal
        title="Yeni Araç Ekle"
        open={vehicleModalVisible}
        onOk={handleSaveVehicle}
        onCancel={() => {
          setVehicleModalVisible(false);
          vehicleForm.resetFields();
          setSelectedBrandId(undefined);
          setModels([]);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={vehicleForm} layout="vertical">
          <Form.Item
            name="plate"
            label="Plaka"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Lütfen plaka girin' }]}
          >
            <Input placeholder="Örn: 34ABC123" />
          </Form.Item>

          <Form.Item
            name="vinChassis"
            label="VIN/Şasi No"
          >
            <Input placeholder="VIN veya şasi numarası" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="brandId"
                label="Marka"
              >
                <Select
                  placeholder="Marka seçin (opsiyonel)"
                  showSearch
                  allowClear
                  onChange={(value) => {
                    setSelectedBrandId(value);
                    vehicleForm.setFieldsValue({ modelId: undefined });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={brands.map(b => ({ value: b.id, label: b.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="modelId"
                label="Model"
              >
                <Select
                  placeholder="Model seçin (opsiyonel)"
                  showSearch
                  allowClear
                  disabled={!selectedBrandId}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={models.map(m => ({ value: m.id, label: m.name }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="make"
                label="Marka (Manuel)"
              >
                <Input placeholder="Marka adı (marka seçilmediyse)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="model"
                label="Model (Manuel)"
              >
                <Input placeholder="Model adı (model seçilmediyse)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="year"
                label="Yıl"
              >
                <InputNumber style={{ width: '100%' }} min={1900} max={new Date().getFullYear() + 1} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="color"
                label="Renk"
              >
                <Input placeholder="Renk" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="currentKm"
                label="Mevcut Km"
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="engineNo"
            label="Motor No"
          >
            <Input placeholder="Motor numarası" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <Input.TextArea rows={3} placeholder="Ek notlar" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Araç Düzenleme Modal - plaka değiştirilemez */}
      <Modal
        title="Araç Bilgilerini Düzenle"
        open={editVehicleModalVisible}
        onOk={handleSaveEditVehicle}
        onCancel={() => {
          setEditVehicleModalVisible(false);
          setEditingVehicle(null);
          setEditVehicleBrandId(undefined);
          setModels([]);
        }}
        okText="Güncelle"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={editVehicleForm} layout="vertical">
          <Form.Item name="plate" label="Plaka">
            <Input disabled placeholder="Plaka (değiştirilemez)" />
          </Form.Item>
          <Form.Item name="vinChassis" label="VIN/Şasi No">
            <Input placeholder="VIN veya şasi numarası" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="brandId" label="Marka">
                <Select
                  placeholder="Marka seçin (opsiyonel)"
                  showSearch
                  allowClear
                  onChange={(value) => {
                    setEditVehicleBrandId(value);
                    editVehicleForm.setFieldsValue({ modelId: undefined });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={brands.map(b => ({ value: b.id, label: b.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="modelId" label="Model">
                <Select
                  placeholder="Model seçin (opsiyonel)"
                  showSearch
                  allowClear
                  disabled={!editVehicleBrandId}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={models.map(m => ({ value: m.id, label: m.name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="make" label="Marka (Manuel)">
                <Input placeholder="Marka adı (marka seçilmediyse)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="model" label="Model (Manuel)">
                <Input placeholder="Model adı (model seçilmediyse)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="year" label="Yıl">
                <InputNumber style={{ width: '100%' }} min={1900} max={new Date().getFullYear() + 1} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="color" label="Renk">
                <Input placeholder="Renk" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="currentKm" label="Mevcut Km">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="engineNo" label="Motor No">
            <Input placeholder="Motor numarası" />
          </Form.Item>
          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={3} placeholder="Ek notlar" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Müşteri Ekleme Modal */}
      <Modal
        title="Yeni Müşteri Ekle"
        open={customerModalVisible}
        onOk={handleSaveCustomer}
        onCancel={() => {
          setCustomerModalVisible(false);
          customerForm.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={customerForm} layout="vertical">
          <Form.Item
            name="firstName"
            label="Ad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Ad giriniz' }]}
          >
            <Input placeholder="Ad" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Soyad"
            normalize={trUpperNormalize}
            rules={[{ required: true, message: 'Soyad giriniz' }]}
          >
            <Input placeholder="Soyad" />
          </Form.Item>

          <Form.Item
            name="companyName"
            label="Firma Adı (Opsiyonel)"
            normalize={trUpperNormalize}
          >
            <Input placeholder="Firma adı (varsa)" />
          </Form.Item>

          <Form.Item
            name="customerCategory"
            label="Müşteri Kategorisi"
            rules={[{ required: true, message: 'Müşteri kategorisi seçiniz' }]}
          >
            <Select placeholder="Müşteri kategorisi seçin">
              {customerCategories.map(cat => (
                <Select.Option key={cat.value} value={cat.value}>
                  {cat.displayName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="taxNumber"
            label="Vergi Numarası"
          >
            <Input placeholder="Vergi numarası" />
          </Form.Item>

          <Form.Item
            name="taxOffice"
            label="Vergi Dairesi"
          >
            <Input placeholder="Vergi dairesi" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
            rules={[{ type: 'email', message: 'Geçerli bir e-posta adresi giriniz' }]}
          >
            <Input placeholder="E-posta adresi" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefon"
            normalize={(val) => {
              const digits = (val || '').replace(/\D/g, '').slice(0, 10);
              if (!digits) return '';
              return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
                [a, b, c, d].filter(Boolean).join(' ')
              );
            }}
          >
            <Input placeholder="532 000 00 00" maxLength={14} />
          </Form.Item>

          <Form.Item
            name="address"
            label="Adres"
          >
            <Input.TextArea placeholder="Adres bilgisi" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceCustomers;
